import React, { useEffect, useState } from 'react'
import {
    Box,
    Button,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@material-ui/core'
import { send_command } from 'components/api'
import { getApiErrorMessage } from 'utils/getApiErrorMessage'

function normalizeClientKey(value) {
    return String(value || '').trim().toLowerCase().replace(/\s+/g, '')
}

function buildKeyRecords(initialKeys) {
    const seen = new Set()
    const records = []
    for (const raw of initialKeys || []) {
        const normalized = normalizeClientKey(raw)
        if (!normalized || seen.has(normalized)) continue
        seen.add(normalized)
        records.push({ raw: String(raw), normalized })
    }
    return records
}

export function ClientKeyTable({ tenantId, initialKeys, onChange }) {
    const [keys, setKeys] = useState([])
    const [adding, setAdding] = useState(false)
    const [newKey, setNewKey] = useState('')
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(null)

    useEffect(() => {
        setKeys(buildKeyRecords(initialKeys))
    }, [initialKeys])

    const addKey = () => {
        const normalizedKey = normalizeClientKey(newKey)
        if (!normalizedKey) return
        if (keys.some(k => k.normalized === normalizedKey)) {
            setError('That client key is already configured')
            return
        }
        setLoading(true)
        setError(null)
        send_command('ADMIN_ADD_CLIENT_KEY', { tenant_id: tenantId, client_key: normalizedKey })
            .then(() => Promise.resolve(onChange?.()))
            .then(() => {
                setNewKey('')
                setAdding(false)
                setLoading(false)
            })
            .catch(ex => {
                setError(getApiErrorMessage(ex, 'Failed to add client key'))
                setLoading(false)
            })
    }

    const removeKey = (keyRecord) => {
        setLoading(true)
        setError(null)
        send_command('ADMIN_REMOVE_CLIENT_KEY', {
            tenant_id: tenantId,
            client_key: keyRecord.raw,
        })
            .then(() => Promise.resolve(onChange?.()))
            .then(() => {
                setLoading(false)
            })
            .catch(ex => {
                setError(getApiErrorMessage(ex, 'Failed to remove client key'))
                setLoading(false)
            })
    }

    return (
        <>
            <Typography variant="body2" color="textSecondary" style={{ marginBottom: 12 }}>
                Client keys are stored in lowercase. Customers can enter the same key in any mix of upper and lower case.
            </Typography>
            {error && (
                <Typography variant="body2" color="error" style={{ marginBottom: 12 }}>
                    {error}
                </Typography>
            )}
            {keys.length > 0 ? (
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Key</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {keys.map(keyRecord => (
                            <TableRow key={keyRecord.normalized}>
                                <TableCell style={{ fontFamily: 'monospace', fontSize: 12 }}>
                                    {keyRecord.normalized}
                                </TableCell>
                                <TableCell align="right">
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        color="secondary"
                                        onClick={() => removeKey(keyRecord)}
                                        disabled={loading}
                                    >
                                        Remove
                                    </Button>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            ) : (
                <Typography variant="body2" color="textSecondary">No client keys configured</Typography>
            )}

            <Box mt={1}>
                {adding ? (
                    <Grid container spacing={1} alignItems="center">
                        <Grid item xs={8}>
                            <TextField
                                value={newKey}
                                onChange={e => {
                                    setNewKey(e.target.value.toLowerCase())
                                    if (error) setError(null)
                                }}
                                label="New client key"
                                helperText="Saved in lowercase"
                                variant="outlined"
                                size="small"
                                fullWidth
                                disabled={loading}
                                onKeyDown={e => { if (e.key === 'Enter') addKey() }}
                                autoFocus
                            />
                        </Grid>
                        <Grid item>
                            <Button
                                variant="contained"
                                color="primary"
                                size="small"
                                onClick={addKey}
                                disabled={loading || !normalizeClientKey(newKey)}
                            >
                                Add
                            </Button>
                        </Grid>
                        <Grid item>
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={() => { setAdding(false); setNewKey('') }}
                                disabled={loading}
                            >
                                Cancel
                            </Button>
                        </Grid>
                    </Grid>
                ) : (
                    <Button variant="outlined" size="small" onClick={() => setAdding(true)} style={{ marginTop: 8 }}>
                        Add key
                    </Button>
                )}
            </Box>
        </>
    )
}
